import { exerciseNow, setExerciseTimeout, clearExerciseTimeout } from "../exercises/pause-clock";
import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useEffect, useMemo, useState } from "react";
import type { MengenMatchExerciseConfig, MengenMatchRunSettings } from "@calega/content";
import type { MengenMatchRun } from "@calega/exercise-engine";
import type { Account } from "../lib/types";
import { useRealtime } from "../realtime/RealtimeContext";
import { ExerciseMenu } from "./ExerciseMenu";
import { ExerciseWaitingCard } from "./ExerciseWaitingCard";
import { ExerciseDurationSelect } from "./ExerciseSettings";
import { abortExerciseRun, submitExerciseAnswer } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";

type Phase = "running" | "feedback" | "complete";

interface MengenMatchAnswer {
  itemId: string;
  value: number;
  correct: boolean;
}

interface MengenMatchState {
  runId: string;
  run: MengenMatchRun;
  index: number;
  phase: Phase;
  endsAt: number | null;
  answers: MengenMatchAnswer[];
  result: { score: number; maxScore: number } | null;
}

const ranges: Array<{ id: MengenMatchRunSettings["range"]; label: string }> = [
  { id: "1-5", label: "1–5" },
  { id: "1-10", label: "1–10" },
  { id: "1-20", label: "1–20" },
];

function gridFontSize(count: number, scale: number) {
  const base = count <= 5 ? 2.5 : count <= 10 ? 2 : count <= 15 ? 1.6 : 1.35;
  return `${Math.max(0.8, base * scale).toFixed(2)}rem`;
}

function MengenObjects({ item }: { item: MengenMatchRun["items"][number] }) {
  const side = item.presentation;
  const canvasStyle = side.canvas
    ? { maxWidth: `${side.canvas.width * 2}px`, aspectRatio: `${side.canvas.width} / ${side.canvas.height}` }
    : undefined;
  return <div
    className={`mengen-match-field ${side.canvas ? "is-scattered" : ""}`}
    style={canvasStyle}
    aria-label="Dargestellte Menge"
  >
    {side.objects.map((object, index) => <span
      className="mengen-match-object"
      key={index}
      style={{
        left: `${object.x}%`,
        top: `${object.y}%`,
        fontSize: object.fontSize ? `${object.fontSize}cqh` : gridFontSize(side.objects.length, object.scale),
        transform: `translate(-50%, -50%) rotate(${object.rotation}deg)`,
      }}
      aria-hidden="true"
    >
      {item.emoji}
    </span>)}
  </div>;
}

export function MengenMatch({ account, config, pageId }: { account: Account; config: MengenMatchExerciseConfig; pageId: string }) {
  const startExerciseRun = useRecommendationRunStarter();
  const completion = useRunCompletion();
  const realtime = useRealtime();
  const facilitator = account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const follower = Boolean(realtime.session && !facilitator);
  const canControl = !realtime.session || facilitator;
  const canAnswer = !realtime.session || facilitator || follower;
  const [mode, setMode] = useState<MengenMatchRunSettings["mode"]>(config.defaultMode);
  const [count, setCount] = useState(config.defaultCount);
  const [durationSeconds, setDurationSeconds] = useState(config.defaultDurationSeconds);
  const [range, setRange] = useState<MengenMatchRunSettings["range"]>(config.defaultRange);
  const [arrangement, setArrangement] = useState<MengenMatchRunSettings["arrangement"]>(config.defaultArrangement);
  const [sizes, setSizes] = useState<MengenMatchRunSettings["sizes"]>(config.defaultSizes);
  const [exercise, setExercise] = useState<MengenMatchState | null>(null);
  useLearningDisplayItem(exercise?.runId, exercise?.index ?? 0, Boolean(exercise && exercise.phase !== "complete"));
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [, tick] = useState(0);
  const score = useMemo(() => exercise?.answers.filter(({ correct }) => correct).length ?? 0, [exercise?.answers]);

  function publish(state: MengenMatchState | null) {
    realtime.publishExercise({ exerciseType: "mengen-match", pageId, state });
  }

  useEffect(() => {
    const synced = realtime.state.exercise as { exerciseType?: string; pageId?: string; state?: MengenMatchState | null } | null;
    if (synced?.exerciseType === "mengen-match" && synced.pageId === pageId) setExercise(synced.state ?? null);
  }, [realtime.state.exercise, pageId]);

  useEffect(() => {
    if (!exercise?.endsAt || exercise.phase === "complete") return;
    const interval = window.setInterval(() => tick((current) => current + 1), 250);
    return () => window.clearInterval(interval);
  }, [exercise?.endsAt, exercise?.phase]);

  useEffect(() => {
    if (busy || !exercise?.endsAt || exercise.phase === "complete" || !canControl) return;
    const timeout = setExerciseTimeout(
      () => finish().catch((error) => setMessage(error.message)),
      Math.max(0, exercise.endsAt - exerciseNow(realtime.serverNow)) + 20,
    );
    return () => clearExerciseTimeout(timeout);
  }, [exercise?.endsAt, exercise?.phase, exercise?.index, exercise?.answers.length, canControl, busy]);

  function settings(): MengenMatchRunSettings {
    return { mode, count, durationSeconds, range, arrangement, sizes };
  }

  async function start(runSettings = settings()) {
    if (busy || !canControl) return;
    setBusy(true);
    try {
      const response = await startExerciseRun<{ runId: string; exercise: MengenMatchRun }>({ contentPageId: pageId, settings: runSettings });
      const next: MengenMatchState = {
        runId: response.runId,
        run: response.exercise,
        index: 0,
        phase: "running",
        endsAt: response.exercise.settings.mode === "time"
          ? exerciseNow(realtime.serverNow) + response.exercise.settings.durationSeconds * 1_000
          : null,
        answers: [],
        result: null,
      };
      setExercise(next);
      setMessage("");
      publish(next);
    } finally {
      setBusy(false);
    }
  }

  async function abort() {
    if (exercise && exercise.phase !== "complete") {
      await abortExerciseRun(exercise.runId);
    }
  }

  async function backToSettings() {
    if (!exercise || busy || !canControl) return;
    setBusy(true);
    try {
      await abort();
      setMode(exercise.run.settings.mode);
      setCount(exercise.run.settings.count);
      setDurationSeconds(exercise.run.settings.durationSeconds);
      setRange(exercise.run.settings.range);
      setArrangement(exercise.run.settings.arrangement);
      setSizes(exercise.run.settings.sizes);
      setExercise(null);
      setMessage("");
      publish(null);
    } finally {
      setBusy(false);
    }
  }

  async function remix() {
    if (!exercise || busy || !canControl) return;
    const runSettings = exercise.run.settings;
    setBusy(true);
    try {
      await abort();
    } finally {
      setBusy(false);
    }
    await start(runSettings);
  }

  async function answer(value: number) {
    if (!exercise || exercise.phase !== "running" || busy || !canAnswer) return;
    const item = exercise.run.items[exercise.index];
    if (!item) return;
    setBusy(true);
    try {
      await submitExerciseAnswer(exercise.runId, { type: "mengen-match", itemId: item.id, value });
      const next: MengenMatchState = {
        ...exercise,
        phase: "feedback",
        answers: [...exercise.answers, { itemId: item.id, value, correct: value === item.target }],
      };
      setExercise(next);
      publish(next);
    } finally {
      setBusy(false);
    }
  }

  async function finish() {
    if (!exercise || exercise.phase === "complete" || busy || !canControl) return;
    await completion.complete(exercise.runId, (result) => {
      const next: MengenMatchState = { ...exercise, result, phase: "complete" };
      setExercise(next);
      publish(next);
      setMessage("");
    });
  }

  async function closeCompletedRun(repeat: boolean) {
    if (!exercise || exercise.phase !== "complete") return;
    if (repeat) await remix();
    else await backToSettings();
  }

  async function next() {
    if (!exercise || exercise.phase !== "feedback" || busy || !canControl) return;
    if ((exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length) {
      await finish();
      return;
    }
    const nextState: MengenMatchState = { ...exercise, index: exercise.index + 1, phase: "running" };
    setExercise(nextState);
    publish(nextState);
  }

  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  if (follower && !exercise) return <ExerciseWaitingCard className="mengen-match-exercise" />;

  if (!exercise) {
    return <section className="mengen-match-exercise">
      <p className="quiz-instructions learning-text">{config.instructions}</p>
      <div className="mathe-settings mengen-match-settings">
        <fieldset>
          <legend>Zahlenraum</legend>
          <div className="mathe-option-grid">
            {ranges.map((item) => <label className="checkbox-label" key={item.id}>
              <input type="radio" name={`${pageId}-mengen-range`} checked={range === item.id} onChange={() => setRange(item.id)} />
              {item.label}
            </label>)}
          </div>
        </fieldset>
        <fieldset>
          <legend>Anordnung</legend>
          <label className="checkbox-label"><input type="radio" name={`${pageId}-mengen-arrangement`} checked={arrangement === "geordnet"} onChange={() => setArrangement("geordnet")} />Geordnet</label>
          <label className="checkbox-label"><input type="radio" name={`${pageId}-mengen-arrangement`} checked={arrangement === "gestreut"} onChange={() => setArrangement("gestreut")} />Gestreut</label>
        </fieldset>
        <fieldset>
          <legend>Größen</legend>
          <label className="checkbox-label"><input type="radio" name={`${pageId}-mengen-sizes`} checked={sizes === "einheitlich"} onChange={() => setSizes("einheitlich")} />Einheitlich</label>
          <label className="checkbox-label"><input type="radio" name={`${pageId}-mengen-sizes`} checked={sizes === "variierend"} onChange={() => setSizes("variierend")} />Variierend</label>
        </fieldset>
        <fieldset>
          <legend>Durchlauf</legend>
          <div className="mathe-run-settings">
            <div className="blitz-choice-row">
              <label className="checkbox-label"><input type="radio" name={`${pageId}-mengen-mode`} checked={mode === "count"} onChange={() => setMode("count")} />Feste Anzahl</label>
              <label className="checkbox-label"><input type="radio" name={`${pageId}-mengen-mode`} checked={mode === "time"} onChange={() => setMode("time")} />Auf Zeit</label>
            </div>
            {mode === "count"
              ? <label>Anzahl Aufgaben<select value={count} onChange={(event) => setCount(Number(event.target.value) as MengenMatchRunSettings["count"])}>{[5, 10, 15, 20].map((value) => <option value={value} key={value}>{value}</option>)}</select></label>
              : <ExerciseDurationSelect value={durationSeconds} onChange={setDurationSeconds} />}
          </div>
        </fieldset>
      </div>
      <button className="primary" disabled={busy || !canControl} onClick={() => start().catch((error) => setMessage(error.message))}>Mengen-Match starten</button>
      {message && <div className="alert info">{message}</div>}
    </section>;
  }

  const item = exercise.run.items[exercise.index];
  const lastAnswer = exercise.answers.at(-1);
  const remaining = exercise.endsAt ? Math.max(0, Math.ceil((exercise.endsAt - exerciseNow(realtime.serverNow)) / 1_000)) : null;
  const completed = exercise.answers.length;
  const last = Boolean(exercise.endsAt && exercise.endsAt <= exerciseNow(realtime.serverNow)) || exercise.index + 1 >= exercise.run.items.length;

  return <section className="mengen-match-exercise">
    {canControl && exercise.phase !== "complete" && <ExerciseMenu>
      <button className="secondary small" disabled={busy} onClick={() => backToSettings().catch((error) => setMessage(error.message))}>← Zurück zu den Einstellungen</button>
      <button className="secondary small" disabled={busy} onClick={() => remix().catch((error) => setMessage(error.message))}>↻ Neu mischen</button>
    </ExerciseMenu>}
    <div className="exercise-progress">{exercise.run.settings.mode === "time" ? `${completed} beantwortet · ${remaining ?? 0} s` : `Aufgabe ${Math.min(exercise.index + 1, exercise.run.items.length)} von ${exercise.run.items.length}`}</div>
    {exercise.phase === "complete"
      ? <>
        <div className="mengen-match-result"><strong>{exercise.result?.score ?? score} von {exercise.result?.maxScore ?? completed} richtig</strong><p>Der Durchlauf ist abgeschlossen.</p></div>
        {canControl && <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setMessage} />}
        {exercise.phase === "complete" && <CompletedRunHistory key={exercise.runId} runId={exercise.runId} />}
      </>
      : item && <>
        <div className="mengen-match-card">
          <p className="mengen-match-prompt learning-text">Wie viele Objekte siehst du?</p>
          <MengenObjects item={item} />
          <div className="mengen-match-options" aria-label="Passende Zahl auswählen">
            {item.options.map((value) => <button
              key={value}
              className={`mengen-match-option ${exercise.phase === "feedback" && value === lastAnswer?.value ? lastAnswer.correct ? "correct" : "wrong" : ""} ${exercise.phase === "feedback" && value === item.target ? "solution" : ""}`}
              disabled={exercise.phase !== "running" || busy || !canAnswer}
              onClick={() => answer(value).catch((error) => setMessage(error.message))}
            >
              {value}
            </button>)}
          </div>
        </div>
        {exercise.phase === "feedback" && lastAnswer && <div className={`answer-feedback ${lastAnswer.correct ? "correct" : "wrong"}`}>{lastAnswer.correct ? "Richtig!" : `Nicht ganz. Es sind ${item.target} Objekte.`}</div>}
        {exercise.phase === "feedback" && (canControl
          ? <div className="mengen-match-actions"><button className="primary" disabled={busy} onClick={() => next().catch((error) => setMessage(error.message))}>{last ? "Auswertung ansehen" : "Nächste Aufgabe"}</button></div>
          : <p className="muted">Die nächste Aufgabe wird von der Sitzungsleitung geöffnet.</p>)}
      </>}
    {message && <div className="alert info">{message}</div>}
  </section>;
}
